import { useQuery, useMutation } from "@apollo/client";
import { useState, useMemo } from "react";
import {
  useQueryParam,
  withDefault,
  NumberParam,
  StringParam
} from "use-query-params";

import { Container } from "@material-ui/core";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import PageTitle from "src/components/PageTitle";
import { TableControls } from "src/components/Table";
import {
  listAllAdmins,
  listAllAdmins_listAdmins
} from "src/graphql/generated/listAllAdmins";
import {
  tenantOwnerDelete,
  tenantOwnerDeleteVariables
} from "src/graphql/generated/tenantOwnerDelete";
import {
  tenantOwnerPromote,
  tenantOwnerPromoteVariables
} from "src/graphql/generated/tenantOwnerPromote";
import {
  ADD_TENANT_OWNER,
  LIST_ALL_ADMINS,
  REMOVE_TENANT_OWNER
} from "src/graphql/tenant";

import AddOwnerDialog from "./AddOwnerDialog";
import OrgOwnerTable from "./OrgOwnerTable";
import RemoveOwnerDialog from "./RemoveOwnerDialog";
import { OrgOwnerSort, ORG_OWNER_SORTS } from "./constants";

function validateSort(sort: string): OrgOwnerSort {
  return (
    ORG_OWNER_SORTS.find(s => s.key === sort)?.key ?? ORG_OWNER_SORTS[0].key
  );
}

type Admin = listAllAdmins_listAdmins;

export default function OrgOwners(): JSX.Element {
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, ORG_OWNER_SORTS[0].key)
  );

  const validatedSort = validateSort(sort);

  const { loading, error, data, refetch } =
    useQuery<listAllAdmins>(LIST_ALL_ADMINS);

  const [addOwnerDialogOpen, toggleAddOwnerDialog] = useState(false);
  const [removingOwner, setRemovingOwner] = useState<Admin | null>(null);

  const [addTenantOwner] = useMutation<
    tenantOwnerPromote,
    tenantOwnerPromoteVariables
  >(ADD_TENANT_OWNER);

  const [removeTenantOwner] = useMutation<
    tenantOwnerDelete,
    tenantOwnerDeleteVariables
  >(REMOVE_TENANT_OWNER);

  const filteredTenantOwners: Admin[] = useMemo(() => {
    const allAdmins = data?.listAdmins ?? [];

    // filter
    const filteredAdmins = allAdmins.filter(
      admin => admin.isTenantOwner && !admin.isStaff
    );

    // sort
    return filteredAdmins.sort((a, b) => {
      switch (validatedSort) {
        case "created_at_asc":
          return a.createdAt - b.createdAt;
        case "created_at_desc":
          return b.createdAt - a.createdAt;
        case "name_asc":
          return (a.fullName || "").localeCompare(b.fullName || "");
        case "name_desc":
          return (b.fullName || "").localeCompare(a.fullName || "");
      }
    });
  }, [data, validatedSort]);

  const rows = filteredTenantOwners.slice(
    page * pageSize,
    (page + 1) * pageSize
  );

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  async function handleAddTenantOwner(values: tenantOwnerPromoteVariables) {
    const { errors } = await addTenantOwner({
      variables: values
    });
    if (errors) {
      throw errors;
    } else {
      await refetch();
      toggleAddOwnerDialog(false);
    }
  }

  async function handleRemoveTenantOwner(values: tenantOwnerDeleteVariables) {
    const { errors } = await removeTenantOwner({
      variables: values
    });
    if (errors) {
      throw errors;
    } else {
      await refetch();
      setRemovingOwner(null);
    }
  }

  return (
    <Container>
      <PageTitle title="Organization Owners" />
      <TableControls
        addButtons={[
          {
            label: "Add Organization Owner",
            onClick: () => toggleAddOwnerDialog(true)
          }
        ]}
        sort={{
          value: sort,
          onChange: value => setSort(value as OrgOwnerSort),
          options: ORG_OWNER_SORTS.map(sort => ({
            value: sort.key,
            label: sort.label
          }))
        }}
      />
      {loading ? (
        <LoadingIndicator />
      ) : (
        <OrgOwnerTable
          rows={rows}
          handleChangePage={setPage}
          handleChangeRowsPerPage={newRowsPerPage => {
            setPageSize(newRowsPerPage);
            setPage(0);
          }}
          rowsPerPage={pageSize}
          page={page}
          totalCount={filteredTenantOwners.length}
          handleRemoveOwner={admin => setRemovingOwner(admin)}
        />
      )}
      <AddOwnerDialog
        open={addOwnerDialogOpen}
        onClose={() => toggleAddOwnerDialog(false)}
        onSubmit={handleAddTenantOwner}
        allAdmins={data?.listAdmins ?? []}
      />
      <RemoveOwnerDialog
        onClose={() => setRemovingOwner(null)}
        onSubmit={handleRemoveTenantOwner}
        admin={removingOwner}
      />
    </Container>
  );
}
